import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../components/Navbar';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Package } from 'lucide-react';
import apiClient from '../utils/api';
import { toast } from 'sonner';
import StageCompletionModal from '../components/operator/StageCompletionModal';
import { getChildCustomerName, formatDate } from '../utils/orderHelpers';

const PRODUCTION_STAGES = [
  'Base Shell Procurement',
  'MS Grinding',
  'Plating',
  'Lathe',
  'Polish',
  'Planning',
  'Engraving',
  'Chroming',
  'Proofing',
  'Dispatch',
];

const OperatorDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [orderStages, setOrderStages] = useState({});
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Column filter states (individual search above each column)
  const [columnFilters, setColumnFilters] = useState({
    customer: '',
    jobName: '',
    circumference: '',
    length: '',
    nos: '',
    dateFrom: '',
    dateTo: '',
  });
  const [sortDate, setSortDate] = useState('desc'); // 'asc' | 'desc' | null
  
  // Stage completion modal state
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedStageName, setSelectedStageName] = useState(null);
  const [selectedStageData, setSelectedStageData] = useState(null);
  const [showStageModal, setShowStageModal] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      const now = new Date();
      const response = await apiClient.get('/dashboard-data', {
        params: { month: now.getMonth() + 1, year: now.getFullYear() },
      });
      const data = response.data;
      setAllOrders(data.orders || []);
      setCustomers(data.customers || []);
      setOrderStages(data.stages || {});
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Apply column filters
  useEffect(() => {
    let filtered = [...allOrders];
    
    if (columnFilters.customer) {
      const query = columnFilters.customer.toLowerCase();
      filtered = filtered.filter(o => 
        (getChildCustomerName(o, customers)).toLowerCase().includes(query)
      );
    }
    if (columnFilters.jobName) {
      const query = columnFilters.jobName.toLowerCase();
      filtered = filtered.filter(o => 
        (o.job_name || '').toLowerCase().includes(query)
      );
    }
    if (columnFilters.circumference) {
      const query = columnFilters.circumference;
      filtered = filtered.filter(o => 
        String(o.specifications?.circumference || '').includes(query)
      );
    }
    if (columnFilters.length) {
      const query = columnFilters.length;
      filtered = filtered.filter(o => 
        String(o.specifications?.length || '').includes(query)
      );
    }
    if (columnFilters.nos) {
      const query = columnFilters.nos;
      filtered = filtered.filter(o => 
        String(o.specifications?.nos_of_cylinders || '').includes(query)
      );
    }
    if (columnFilters.dateFrom) {
      const from = new Date(columnFilters.dateFrom);
      filtered = filtered.filter(o => new Date(o.created_at) >= from);
    }
    if (columnFilters.dateTo) {
      const to = new Date(columnFilters.dateTo);
      to.setHours(23, 59, 59, 999);
      filtered = filtered.filter(o => new Date(o.created_at) <= to);
    }

    // Sort by date
    if (sortDate) {
      filtered.sort((a, b) => {
        const da = new Date(a.created_at || 0);
        const db = new Date(b.created_at || 0);
        return sortDate === 'desc' ? db - da : da - db;
      });
    }
    
    setOrders(filtered);
  }, [columnFilters, allOrders, sortDate, customers]);

  const handleStageClick = (order, stageName) => {
    if (order.status === 'cancelled') return;
    
    const stages = orderStages[order.id] || [];
    const stageData = stages.find(s => s.stage_name === stageName);
    
    setSelectedOrder(order);
    setSelectedStageName(stageName);
    setSelectedStageData(stageData);
    setShowStageModal(true);
  };

  const handleStageUpdated = () => {
    setShowStageModal(false);
    setSelectedOrder(null);
    setSelectedStageName(null);
    setSelectedStageData(null);
    fetchOrders();
  };

  const getStageStatus = (orderId, stageName) => {
    const stages = orderStages[orderId] || [];
    const stage = stages.find(s => s.stage_name === stageName);
    return stage?.status || 'pending';
  };

  const getStageDetails = (orderId, stageName) => {
    const stages = orderStages[orderId] || [];
    return stages.find(s => s.stage_name === stageName);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return 'D';
      case 'in_progress':
        return 'P';
      default:
        return '-';
    }
  };

  const getOverallProgress = (orderId, order) => {
    // Dispatch is derived from invoice presence (no production_stages doc for it).
    const stages = (orderStages[orderId] || []).filter(s => s.stage_name !== 'Order Received' && s.stage_name !== 'Dispatch');
    const completedStages = stages.filter(s => s.status === 'completed').length;
    const dispatchDone = !!order?.dispatch_completed_at ? 1 : 0;
    return `${completedStages + dispatchDone}/${PRODUCTION_STAGES.length}`;
  };

  const clearFilters = () => {
    setColumnFilters({
      customer: '',
      jobName: '',
      circumference: '',
      length: '',
      nos: '',
    });
  };

  const hasActiveFilters = Object.values(columnFilters).some(v => v !== '');

  return (
    <div data-testid="operator-dashboard" className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div></div>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Clear all filters
            </button>
          )}
        </div>

        {/* Orders Grid */}
        {loading ? (
          <div className="text-center py-12 text-slate-600">Loading orders...</div>
        ) : orders.length === 0 ? (
          <Card className="p-12 text-center">
            <Package className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No Orders Found</h3>
            <p className="text-slate-500">
              {hasActiveFilters ? 'Try adjusting your filters' : 'No orders available'}
            </p>
          </Card>
        ) : (
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  {/* Column Headers */}
                  <tr className="bg-slate-100 border-b border-slate-200">
                    <th
                      className="sticky left-0 bg-slate-100 px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider min-w-[80px] border-r border-slate-200 cursor-pointer select-none"
                      onClick={() => setSortDate(prev => prev === 'desc' ? 'asc' : prev === 'asc' ? null : 'desc')}
                    >
                      <div className="flex items-center gap-1">
                        Date
                        {sortDate && <span className="text-[9px] text-slate-900">{sortDate === 'desc' ? '\u25BC' : '\u25B2'}</span>}
                      </div>
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider min-w-[140px]">
                      Customer
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider min-w-[200px]">
                      Job Name
                    </th>
                    <th className="px-2 py-2 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider min-w-[70px]">
                      Circ.
                    </th>
                    <th className="px-2 py-2 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider min-w-[70px]">
                      Length
                    </th>
                    <th className="px-2 py-2 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider min-w-[55px]">
                      Nos
                    </th>
                    {PRODUCTION_STAGES.map((stage) => (
                      <th 
                        key={stage} 
                        className="px-1 py-2 text-center text-[9px] font-semibold text-slate-600 uppercase tracking-wider min-w-[70px] max-w-[80px]"
                        title={stage}
                      >
                        <div className="leading-tight whitespace-normal">
                          {stage}
                        </div>
                      </th>
                    ))}
                    <th className="px-2 py-2 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider min-w-[70px]">
                      Progress
                    </th>
                  </tr>
                  {/* Column Filters Row */}
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="sticky left-0 bg-slate-50 px-2 py-2 border-r border-slate-200">
                      <div className="space-y-1">
                        <Input type="date" value={columnFilters.dateFrom} onChange={(e) => setColumnFilters({...columnFilters, dateFrom: e.target.value})} className="h-6 text-[10px] w-full" title="From" />
                        <Input type="date" value={columnFilters.dateTo} onChange={(e) => setColumnFilters({...columnFilters, dateTo: e.target.value})} className="h-6 text-[10px] w-full" title="To" />
                      </div>
                    </th>
                    <th className="px-2 py-2">
                      <Input
                        data-testid="filter-customer"
                        placeholder="Filter..."
                        value={columnFilters.customer}
                        onChange={(e) => setColumnFilters({...columnFilters, customer: e.target.value})}
                        className="h-7 text-xs"
                      />
                    </th>
                    <th className="px-2 py-2">
                      <Input
                        data-testid="filter-job-name"
                        placeholder="Filter..."
                        value={columnFilters.jobName}
                        onChange={(e) => setColumnFilters({...columnFilters, jobName: e.target.value})}
                        className="h-7 text-xs"
                      />
                    </th>
                    <th className="px-1 py-2">
                      <Input
                        data-testid="filter-circumference"
                        placeholder="..."
                        value={columnFilters.circumference}
                        onChange={(e) => setColumnFilters({...columnFilters, circumference: e.target.value})}
                        className="h-7 text-xs text-center"
                      />
                    </th>
                    <th className="px-1 py-2">
                      <Input
                        data-testid="filter-length"
                        placeholder="..."
                        value={columnFilters.length}
                        onChange={(e) => setColumnFilters({...columnFilters, length: e.target.value})}
                        className="h-7 text-xs text-center"
                      />
                    </th>
                    <th className="px-1 py-2">
                      <Input
                        data-testid="filter-nos"
                        placeholder="..."
                        value={columnFilters.nos}
                        onChange={(e) => setColumnFilters({...columnFilters, nos: e.target.value})}
                        className="h-7 text-xs text-center"
                      />
                    </th>
                    {/* Empty cells for stage columns - no filters */}
                    {PRODUCTION_STAGES.map((stage) => (
                      <th key={stage} className="px-1 py-2"></th>
                    ))}
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {orders.map((order) => {
                    const isCancelled = order.status === 'cancelled';
                    return (
                    <tr 
                      key={order.id} 
                      data-testid={`order-row-${order.id}`}
                      className={`hover:bg-slate-50 transition-colors ${isCancelled ? 'opacity-60 bg-red-50' : ''}`}
                    >
                      <td className={`sticky left-0 px-3 py-3 border-r border-slate-200 text-xs text-slate-500 whitespace-nowrap ${isCancelled ? 'bg-red-50' : 'bg-white'}`}>
                        {formatDate(order.created_at)}
                      </td>
                      <td className={`px-3 py-3 ${isCancelled ? 'bg-red-50' : ''}`}>
                        <div className="font-semibold text-slate-900 text-sm truncate max-w-[150px]" title={getChildCustomerName(order, customers)}>
                          {getChildCustomerName(order, customers)}
                        </div>
                      </td>
                      
                      <td className="px-3 py-3">
                        <div className="text-sm text-slate-700 whitespace-normal break-words min-w-[180px] max-w-[300px]">
                          {order.job_name || '-'}
                        </div>
                      </td>
                      
                      <td className="px-2 py-3 text-center">
                        <span className="text-sm text-slate-700">{order.specifications?.circumference || '-'}</span>
                      </td>
                      
                      <td className="px-2 py-3 text-center">
                        <span className="text-sm text-slate-700">{order.specifications?.length || '-'}</span>
                      </td>
                      
                      <td className="px-2 py-3 text-center">
                        <span className="text-sm font-medium text-slate-900">{order.specifications?.nos_of_cylinders || '-'}</span>
                      </td>
                      
                      {PRODUCTION_STAGES.map((stage) => {
                        const status = getStageStatus(order.id, stage);
                        const details = getStageDetails(order.id, stage);
                        const totalCompleted = details?.total_completed || 0;
                        const totalRequired = order.specifications?.nos_of_cylinders || 0;
                        
                        return (
                          <td 
                            key={stage} 
                            className="px-1 py-3 text-center"
                            title={`${stage}\nCompleted: ${totalCompleted}/${totalRequired}${details?.completions?.length ? '\n' + details.completions.map(c => `${c.operator_name}: ${c.quantity}`).join('\n') : ''}`}
                          >
                            <div 
                              data-testid={`stage-cell-${order.id}-${stage.replace(/\s+/g, '-')}`}
                              onClick={() => handleStageClick(order, stage)}
                              className={`w-7 h-7 mx-auto rounded flex items-center justify-center font-bold text-xs transition-all ${
                                isCancelled
                                  ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                  : status === 'completed'
                                  ? 'bg-emerald-100 text-emerald-700 cursor-pointer hover:bg-emerald-200 hover:ring-2 hover:ring-emerald-300'
                                  : status === 'in_progress'
                                  ? 'bg-blue-100 text-blue-700 cursor-pointer hover:bg-blue-200 hover:ring-2 hover:ring-blue-300'
                                  : 'bg-slate-100 text-slate-400 cursor-pointer hover:bg-slate-200 hover:ring-2 hover:ring-slate-300'
                              }`}
                            >
                              {getStatusBadge(status)}
                            </div>
                            {totalCompleted > 0 && status !== 'completed' && !isCancelled && (
                              <div className="text-[9px] text-blue-600 mt-0.5">
                                {totalCompleted}/{totalRequired}
                              </div>
                            )}
                          </td>
                        );
                      })}
                      
                      <td className="px-2 py-3 text-center">
                        {isCancelled ? (
                          <span className="inline-block px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded">
                            CANCELLED
                          </span>
                        ) : (
                          <span className="inline-block px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded">
                            {getOverallProgress(order.id, order)}
                          </span>
                        )}
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Stage Completion Modal */}
      {showStageModal && selectedOrder && selectedStageName && (
        <StageCompletionModal
          isOpen={showStageModal}
          onClose={() => {
            setShowStageModal(false);
            setSelectedOrder(null);
            setSelectedStageName(null);
            setSelectedStageData(null);
          }}
          order={selectedOrder}
          stageName={selectedStageName}
          stageData={selectedStageData}
          onSuccess={handleStageUpdated}
        />
      )}
    </div>
  );
};

export default OperatorDashboard;
